import { useState, type ComponentType, type SVGProps } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from '../lib/api';
import { useLogout, useMe } from '../lib/auth';
import { Alert } from '../ui/Card';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import {
  IconCard,
  IconChart,
  IconDashboard,
  IconMenu,
  IconOrders,
  IconPalette,
  IconPlug,
  IconSettings,
  IconShield,
  IconTruck,
} from '../ui/icons';
import { UsageMeter } from '../features/billing/UsageMeter';

type Item = { to: string; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> };

const MAIN: Item[] = [
  { to: '/dashboard', label: 'Dashboard', icon: IconDashboard },
  { to: '/orders', label: 'Orders', icon: IconOrders },
  { to: '/shipments', label: 'Shipments', icon: IconTruck },
  { to: '/analytics', label: 'Analytics', icon: IconChart },
];
const SETUP: Item[] = [
  { to: '/tracking-page', label: 'Tracking page', icon: IconPalette },
  { to: '/integrations', label: 'Integrations', icon: IconPlug },
  { to: '/billing', label: 'Billing', icon: IconCard },
  { to: '/settings', label: 'Settings', icon: IconSettings },
];

function NavGroup({
  title,
  items,
  onNavigate,
}: {
  title?: string;
  items: Item[];
  onNavigate: () => void;
}) {
  return (
    <div>
      {title && (
        <p className="mb-1 px-3 text-[11px] font-semibold tracking-wider text-ink-400 uppercase">
          {title}
        </p>
      )}
      <ul className="space-y-0.5">
        {items.map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <NavLink
              to={to}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                }`
              }
            >
              <Icon />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VerifyBanner() {
  const toast = useToast();
  const resend = useMutation({
    mutationFn: () => apiPost('/auth/resend-verification'),
    onSuccess: () => toast('Confirmation email sent. Check your inbox.'),
    onError: (e) => toast(e.message, 'error'),
  });
  return (
    <Alert
      tone="warning"
      action={
        <Button
          size="sm"
          variant="secondary"
          loading={resend.isPending}
          onClick={() => resend.mutate()}
        >
          Resend email
        </Button>
      }
    >
      Confirm your email to connect a store and publish your tracking page.
    </Alert>
  );
}

/**
 * Inside Shopify admin: Shopify renders the navigation (from <s-app-nav>) and the account
 * controls, so TrackFlow shows only the page content.
 */
function EmbeddedShell() {
  return (
    <>
      <s-app-nav>
        <s-link href="/shopify/dashboard" rel="home">
          Dashboard
        </s-link>
        {[...MAIN.slice(1), ...SETUP].map((item) => (
          <s-link key={item.to} href={`/shopify${item.to}`}>
            {item.label}
          </s-link>
        ))}
      </s-app-nav>
      <main id="main" className="min-w-0 px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-4">
          <Outlet />
        </div>
      </main>
    </>
  );
}

export function AppShell({ embedded = false }: { embedded?: boolean }) {
  const { data: me } = useMe();
  const logout = useLogout();
  const [open, setOpen] = useState(false);

  if (!me) return null;
  if (embedded) return <EmbeddedShell />;
  const initials = (me.user.name ?? me.user.email).slice(0, 1).toUpperCase();

  const sidebar = (
    <div className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-5">
      <div className="flex items-center gap-2.5 px-3">
        <img src="/favicon.svg" alt="" className="size-7" />
        <span className="text-[15px] font-semibold tracking-tight">TrackFlow</span>
      </div>
      {me.merchant && (
        <div className="mx-1 rounded-lg border border-ink-200 bg-white px-3 py-2">
          <p className="truncate text-sm font-semibold">{me.merchant.name}</p>
          <p className="truncate text-xs text-ink-500">/t/{me.merchant.slug}</p>
        </div>
      )}
      <nav aria-label="Main" className="flex flex-1 flex-col gap-6">
        {me.merchant && <NavGroup items={MAIN} onNavigate={() => setOpen(false)} />}
        {me.merchant && <NavGroup title="Setup" items={SETUP} onNavigate={() => setOpen(false)} />}
        {!me.merchant && <NavGroup items={[SETUP[3]!]} onNavigate={() => setOpen(false)} />}
        {me.user.isPlatformAdmin && (
          <NavGroup
            title="Internal"
            items={[{ to: '/admin', label: 'Admin', icon: IconShield }]}
            onNavigate={() => setOpen(false)}
          />
        )}
      </nav>
      {me.merchant && <UsageMeter />}
      <div className="flex items-center gap-3 border-t border-ink-200 px-2 pt-4">
        <span
          className="flex size-8 items-center justify-center rounded-full bg-brand-700 text-sm font-semibold text-white"
          aria-hidden="true"
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{me.user.name ?? 'Account'}</p>
          <p className="truncate text-xs text-ink-500">{me.user.email}</p>
        </div>
        <button
          type="button"
          onClick={logout}
          className="rounded-md px-2 py-1 text-xs font-medium text-ink-600 hover:bg-ink-100"
        >
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[256px_minmax(0,1fr)]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-dvh border-r border-ink-200 bg-ink-100/60 lg:block">
        {sidebar}
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-ink-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-center gap-2 font-semibold">
          <img src="/favicon.svg" alt="" className="size-6" /> TrackFlow
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md p-1.5 text-ink-700 hover:bg-ink-100"
          aria-label="Open menu"
          aria-expanded={open}
        >
          <IconMenu />
        </button>
      </header>
      {open && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-ink-50 shadow-xl">
            {sidebar}
          </div>
        </div>
      )}

      <main id="main" className="min-w-0 px-4 py-6 sm:px-8 lg:py-8">
        <div className="mx-auto max-w-6xl space-y-4">
          {!me.user.emailVerified && <VerifyBanner />}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
